import { inject, Injectable } from '@angular/core';

/**
 * libs
 */
import { jsPDF } from 'jspdf';
import autoTable, { RowInput, UserOptions } from 'jspdf-autotable';
import { TranslateService } from '@ngx-translate/core';

/**
 * services
 */
import { LangService } from '../../lang';
import { LayoutLang } from '../../Layout/enums/layout-config';

/**
 * consts
 */
import { ExportTranslateMapper } from '../consts';

/**
 * interfaces
 */
import { ExportFormat, ExportSectionLayout } from '../types';
import {
  IExportSection,
  IPdfElementExportMetadata,
  IPdfExporter,
  IPdfExportRequest,
} from '../interfaces';

/**
 * utils
 */
import { ensureFileExtension } from './export-row.utils';
import { applyPdfBidi, loadArabicFont } from '../utilities/pdf-arabic.util';
import { captureElementToCanvas, resolveCaptureElement } from '../utilities/dom-capture.util';
import {
  chunkExportTableToBands,
  chunkExportTableToWidth,
  IExportSectionTable,
  renderExportSection,
  toExportSections,
} from './export-section.utils';
import { drawPdfFooter, drawPdfHeader, IExportLogo, PDF_CHROME, resolveExportLogo } from '../utilities/pdf-chrome.util';

/**
 * providers
 */
import { EXPORT_BRANDING } from '../provider/export-branding.token';
import { EXPORT_BRANDING_DEFAULT_OPTIONS } from '../provider/export-branding';

/**
 * One section, rendered into the tables it needs and told how to place them.
 */
interface ISectionPages {
  /**
   * The section's tables, in reading order, stacked down the section's page.
   *
   * @remarks
   * Only a `wrapped` section produces more than one: its bands. `rows` and
   * `fields` are each drawn as a single table that paginates itself by row.
   */
  tables: IExportSectionTable[];
  /** Heading drawn once above the section, or `''` for none. */
  title: string;
  /** The section's layout, which selects how its tables are styled. */
  layout: ExportSectionLayout;
}

/**
 * PDF export strategy implementation.
 */
@Injectable()
export class PdfExporterService implements IPdfExporter {

  /**
   * The format of the exporter.
   */
  public readonly format: ExportFormat = 'pdf';

  /**
   * Document chrome — logo, footer caption, Arabic font.
   */
  private readonly _branding = inject(EXPORT_BRANDING, { optional: true }) ?? EXPORT_BRANDING_DEFAULT_OPTIONS;

  /**
   * Active app language.
   *
   * @remarks
   * Read from {@link LangService} rather than from `EXPORT_OPTIONS.locale`,
   * which is a provider-level default that never tracks the language switch.
   */
  private readonly _langService: LangService = inject(LangService);

  /** Resolves the module's own copy — section fallbacks and the page counter. */
  private readonly _translate: TranslateService = inject(TranslateService);

  /** Translate key map for the copy this module owns. */
  private readonly _translateKeyMap = ExportTranslateMapper;

  /**
   * Width one character of body text occupies, in points, at the body font
   * size. Used to estimate how many columns fit a page.
   */
  private static readonly _APPROX_CHAR_WIDTH = 5.2;

  /**
   * Narrowest a data column is allowed to become before the table is split.
   */
  private static readonly _MIN_COLUMN_WIDTH = 64;

  /** Font size of a section heading. */
  private static readonly _SECTION_TITLE_FONT_SIZE = 11;

  /** Vertical space a section heading takes, in points. */
  private static readonly _SECTION_TITLE_GAP = 18;

  /** Vertical space between two stacked bands of a `wrapped` section. */
  private static readonly _BAND_GAP = 5;

  /** Vertical space between two sections stacked on one page (`sectionBreak: 'none'`). */
  private static readonly _SECTION_GAP = 14;

  /**
   * Space a stacked section needs below the previous one — its heading, header
   * row and first rows — before it may start on the same page, so a heading is
   * never left alone at the foot of a page.
   */
  private static readonly _SECTION_KEEP_TOGETHER = 60;

  /** Width of every cell border, in points. */
  private static readonly _CELL_BORDER_WIDTH = 0.5;

  /** Color of every cell border. */
  private static readonly _CELL_BORDER_COLOR: [number, number, number] = [180, 180, 180];

  /**
   * Fill of a header row — the dark grey the reports this module replaces
   * used, kept so an exported page still reads as the same document.
   */
  private static readonly _TABLE_HEAD_FILL: [number, number, number] = [69, 69, 69];

  /** Text color of a header row, against {@link _TABLE_HEAD_FILL}. */
  private static readonly _TABLE_HEAD_TEXT: [number, number, number] = [255, 255, 255];

  /**
   * Export the data to a PDF file.
   *
   * @param request - The export request.
   * @throws When an unknown content type is requested.
   *
   * @remarks
   * `metadata` is the {@link IPdfExportMetadata} union, so the `contentType`
   * check below is a real narrowing rather than a cast: the capture renderer
   * receives metadata on which `element` is a required property, and cannot be
   * reached without one.
   */
  public async export<TRow = unknown>(request: IPdfExportRequest<TRow>): Promise<void> {

    const metadata = request.metadata;
    const contentType = metadata?.contentType ?? 'table';

    if (contentType === 'table') {
      await this._generatePdfTable(request);

      return;
    }

    if (metadata?.contentType === 'element') {
      await this._generatePdfElement(request, metadata);

      return;
    }

    throw new Error(`PDF content type "${contentType}" is not supported.`);
  }

  /**
   * Generates PDF output from a rendered DOM region.
   *
   * @param request - The export request.
   * @param metadata - The request's metadata, already narrowed to the capture
   * form by the caller. Passed separately because narrowing a property does not
   * narrow the object that holds it — taking it as an argument is what keeps
   * `element` a required, non-optional read here.
   *
   * @remarks
   * The escape hatch from the table renderer's bidi approximation: the browser
   * paints the capture, so Arabic shaping, word order and RTL layout are exact.
   * A capture taller than one page is sliced across pages rather than squashed
   * to fit, which is what keeps a tall card legible.
   */
  private async _generatePdfElement<TRow = unknown>(
    request: IPdfExportRequest<TRow>,
    metadata: IPdfElementExportMetadata,
  ): Promise<void> {

    const element = resolveCaptureElement(metadata.element);

    const canvas = await captureElementToCanvas(element, {
      scale: metadata.scale,
      excludeSelectors: metadata.excludeSelectors,
      backgroundColor: metadata.backgroundColor,
    });

    const isRtl = this._langService.lang === LayoutLang.Ar;
    const orientation = metadata.orientation
      ?? (canvas.width > canvas.height ? 'landscape' : 'portrait');

    const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4' });

    // The header title is drawn with `doc.text`, so it needs the same font and
    // ordering treatment the table renderer applies — the captured body does
    // not, having been painted by the browser.
    if (isRtl) {
      await loadArabicFont(doc, this._branding);
    }

    const logo = await resolveExportLogo(this._branding);
    const documentTitle = isRtl ? applyPdfBidi(request.title ?? '') : (request.title ?? '');

    this._placeCapture(doc, canvas);
    this._stampChrome(doc, { title: documentTitle, logo, isRtl });

    this._output(doc, request);
  }

  /**
   * Draws a capture into the page frame, spilling onto further pages when it
   * is taller than one page.
   *
   * @param doc - The document being written.
   * @param canvas - The painted capture.
   */
  private _placeCapture(doc: jsPDF, canvas: HTMLCanvasElement): void {

    const usableWidth = doc.internal.pageSize.getWidth() - (PDF_CHROME.MARGIN * 2);
    const usableHeight = doc.internal.pageSize.getHeight() - PDF_CHROME.CONTENT_TOP - PDF_CHROME.MARGIN;

    // One ratio for both axes, so the capture is never stretched.
    const ratio = usableWidth / canvas.width;
    const scaledHeight = canvas.height * ratio;

    if (scaledHeight <= usableHeight) {
      doc.addImage(
        canvas.toDataURL('image/png'),
        'PNG',
        PDF_CHROME.MARGIN,
        PDF_CHROME.CONTENT_TOP,
        usableWidth,
        scaledHeight,
      );

      return;
    }

    // Slice in source pixels rather than redrawing the whole capture at an
    // offset: a single tall image offset upward would still be embedded once
    // per page, multiplying the file size by the page count.
    const sliceHeight = Math.floor(usableHeight / ratio);
    let offset = 0;
    let page = 0;

    while (offset < canvas.height) {
      const height = Math.min(sliceHeight, canvas.height - offset);

      if (page > 0) {
        doc.addPage();
      }

      doc.addImage(
        this._sliceCanvas(canvas, offset, height),
        'PNG',
        PDF_CHROME.MARGIN,
        PDF_CHROME.CONTENT_TOP,
        usableWidth,
        height * ratio,
      );

      offset += height;
      page += 1;
    }
  }

  /**
   * Cuts a horizontal band out of a capture.
   *
   * @param canvas - The full capture.
   * @param offset - Top edge of the band, in source pixels.
   * @param height - Height of the band, in source pixels.
   * @returns The band as a PNG data URI.
   * @throws When a 2D context cannot be obtained.
   */
  private _sliceCanvas(canvas: HTMLCanvasElement, offset: number, height: number): string {

    const slice = document.createElement('canvas');

    slice.width = canvas.width;
    slice.height = height;

    const context = slice.getContext('2d');

    if (context == null) {
      throw new Error('A 2D canvas context could not be created to paginate the capture.');
    }

    context.drawImage(canvas, 0, offset, canvas.width, height, 0, 0, canvas.width, height);

    return slice.toDataURL('image/png');
  }

  /**
   * Generates PDF output for table-based content.
   *
   * @param request - The export request.
   *
   * @remarks
   * One section per page by default; with `sectionBreak: 'none'` sections stack
   * down the page and break only where the next one would not fit. Each section
   * is headed by its title unless `sectionHeading: 'hide'`. A `fields`
   * section is transposed into a two-column
   * `Field | Value` table, which is what keeps a 23-field record on a single
   * page instead of spreading it across four. A `wrapped` section keeps the
   * classic table but folds it into bands that stack down that one page. A
   * `rows` section keeps the classic table whole — every column on the page,
   * text wrapped to fit — and spills onto further pages by row.
   */
  private async _generatePdfTable<TRow = unknown>(request: IPdfExportRequest<TRow>): Promise<void> {

    const sections = toExportSections(request);
    const isRtl = this._langService.lang === LayoutLang.Ar;
    // Only the classic table needs the extra width; both single-record layouts
    // read down the page and are set portrait, as the reports they replace were.
    const orientation = request.metadata?.orientation
      ?? (sections.every((section) => section.layout === 'fields' || section.layout === 'wrapped')
        ? 'portrait'
        : 'landscape');

    const doc = new jsPDF({ orientation, unit: 'pt', format: 'a4' });

    // Arabic needs a font carrying its glyphs before a single cell is drawn.
    // Shaping itself is jsPDF's own `processArabic`, already registered on its
    // `preProcessText` event — only the glyphs and the ordering are ours.
    if (isRtl) {
      await loadArabicFont(doc, this._branding);
    }

    const bidi = isRtl ? applyPdfBidi : undefined;
    const logo = await resolveExportLogo(this._branding);
    const documentTitle = bidi?.(request.title ?? '') ?? request.title ?? '';

    const tableMetadata = request.metadata?.contentType === 'element' ? undefined : request.metadata;
    const sectionBreak = tableMetadata?.sectionBreak ?? 'page';
    const hideHeadings = tableMetadata?.sectionHeading === 'hide';

    let cursorY: number = PDF_CHROME.CONTENT_TOP;

    sections
      .map((section, index) => this._buildTables(doc, section, index, bidi))
      // An empty title draws no heading, and takes no space above the table.
      .map((group) => (hideHeadings ? { ...group, title: '' } : group))
      .forEach((group, index) => {
        // The page break belongs between *sections* — a section's own bands
        // stack, which is what keeps one meter on one page.
        if (index > 0) {
          cursorY = sectionBreak === 'page' || !this._fitsBelow(doc, cursorY)
            ? this._openPage(doc)
            : cursorY + PdfExporterService._SECTION_GAP;
        }

        cursorY = this._drawSectionPages(doc, group, isRtl, cursorY);
      });

    this._stampChrome(doc, { title: documentTitle, logo, isRtl });

    this._output(doc, request);
  }

  /**
   * Renders one section into the page-sized tables it needs.
   *
   * @param doc - The document, used to measure the usable page width.
   * @param section - The section to render.
   * @param index - Its position, used for the fallback title.
   * @param bidi - Per-cell reordering, applied in Arabic only.
   * @returns The section's tables and how to place them.
   */
  private _buildTables<TRow>(
    doc: jsPDF,
    section: IExportSection<TRow>,
    index: number,
    bidi: ((value: string) => string) | undefined,
  ): ISectionPages {

    // A wrapped section carries its identity in its own cells — Unit Name and
    // Bill Number are columns of the table — so it is given no positional name
    // to fall back on, and prints no heading unless the caller asked for one.
    const fallbackTitle = section.layout === 'wrapped'
      ? ''
      : `${this._translate.instant(this._translateKeyMap.SECTION_FALLBACK_TITLE())} ${index + 1}`;

    const table = renderExportSection(section, fallbackTitle, bidi);

    // A transposed section is two columns wide by construction — there is
    // nothing to fold, and that is the whole point of the layout.
    if (section.layout === 'fields') {
      return { tables: [table], title: table.title, layout: 'fields' };
    }

    if (section.layout === 'wrapped') {
      // Declared banding wins: only the caller knows which fields belong on a
      // line together. The width fit stays the fallback for every section that
      // has no opinion about it.
      const usableWidth = doc.internal.pageSize.getWidth() - (PDF_CHROME.MARGIN * 2);

      const bands = section.bands != null && section.bands.length > 0
        ? chunkExportTableToBands(table, section.bands)
        : chunkExportTableToWidth(table, this._measureColumnWidths(table), usableWidth);

      return { tables: bands, title: table.title, layout: 'wrapped' };
    }

    // The classic table is never cut by column: autoTable fits every column to
    // the page and wraps the text inside them, and a list too long for one page
    // spills onto the next by *row*, its header repeated. Slicing the columns
    // instead scattered one record over several pages, where no reader can put
    // it back together.
    return { tables: [table], title: table.title, layout: 'rows' };
  }

  /**
   * Estimates the natural width of every column, in points.
   *
   * @param table - The rendered table.
   * @returns One width per column, never below the floor.
   *
   * @remarks
   * An estimate from the longest cell rather than a real text measurement: its
   * one caller only needs to know how many columns *fit* a band, and measuring
   * 23 columns against the loaded font per section would cost far more than the
   * approximation is worth.
   */
  private _measureColumnWidths(table: IExportSectionTable): number[] {

    return table.head.map((header, columnIndex) => {
      const longest = table.body.reduce(
        (max, row) => Math.max(max, (row[columnIndex] ?? '').length),
        header.length,
      );

      return Math.max(
        PdfExporterService._MIN_COLUMN_WIDTH,
        longest * PdfExporterService._APPROX_CHAR_WIDTH,
      );
    });
  }

  /**
   * Whether a stacked section still has room to start below `y` on this page.
   *
   * @param doc - The document being written.
   * @param y - Bottom edge of the previous section.
   * @returns True when the next section's heading and first rows fit.
   */
  private _fitsBelow(doc: jsPDF, y: number): boolean {

    const bottom = doc.internal.pageSize.getHeight() - PDF_CHROME.MARGIN;

    return y + PdfExporterService._SECTION_GAP + PdfExporterService._SECTION_KEEP_TOGETHER <= bottom;
  }

  /**
   * Opens a new page for the next section.
   *
   * @param doc - The document being written.
   * @returns The first y position content may occupy on it.
   */
  private _openPage(doc: jsPDF): number {

    doc.addPage();

    return PDF_CHROME.CONTENT_TOP;
  }

  /**
   * Draws one section onto the current page, from `startY` down.
   *
   * @param doc - The document being written.
   * @param group - The section's tables and its layout.
   * @param isRtl - Whether the active language is right-to-left.
   * @param startY - First y position the section may occupy.
   * @returns The y position of the section's bottom edge, on the page it ends on.
   *
   * @remarks
   * The heading is drawn once for the whole section, not once per table: the
   * several tables a section can hold are the bands of one record, and
   * repeating the meter name above each of them would read as several records.
   *
   * Nothing here opens a page. A table taller than the page left beneath it
   * paginates itself — autoTable carries it onto the next page, repeating its
   * header — which is what keeps a long list readable without this method
   * having to guess where the rows run out.
   */
  private _drawSectionPages(doc: jsPDF, group: ISectionPages, isRtl: boolean, startY: number): number {

    let bottomY = this._drawSectionHeading(doc, group.title, isRtl, startY);
    let cursorY = bottomY;

    group.tables.forEach((table) => {
      bottomY = this._drawTable(doc, table, isRtl, cursorY, group.layout);
      cursorY = bottomY + PdfExporterService._BAND_GAP;
    });

    return bottomY;
  }

  /**
   * Draws one table and reports where the next one may begin.
   *
   * @param doc - The document being written.
   * @param table - The table to draw.
   * @param isRtl - Whether the active language is right-to-left.
   * @param startY - First y position the table may occupy.
   * @param layout - Layout of the section this table belongs to, which is what
   *   selects its styling.
   * @returns The y position of the table's bottom edge.
   */
  private _drawTable(
    doc: jsPDF,
    table: IExportSectionTable,
    isRtl: boolean,
    startY: number,
    layout: ExportSectionLayout,
  ): number {

    const hasHead = table.head.length > 0;

    // autoTable reports the bottom edge through its page hook; seeded with the
    // start so a table that somehow draws nothing still returns a usable y.
    let finalY = startY;

    autoTable(doc, {
      ...this._resolveTableStyles(layout, isRtl, doc.getFont().fontName),
      head: hasHead ? [table.head] : undefined,
      body: table.body as RowInput[],
      startY,
      margin: {
        top: PDF_CHROME.CONTENT_TOP,
        bottom: PDF_CHROME.MARGIN,
        left: PDF_CHROME.MARGIN,
        right: PDF_CHROME.MARGIN,
      },
      // A transposed table has no header row, so the field column carries the
      // emphasis instead — otherwise every row reads as undifferentiated text.
      columnStyles: hasHead ? {} : { 0: { fontStyle: 'bold', cellWidth: 'wrap' } },
      didDrawPage: (data) => {
        finalY = data.cursor?.y ?? data.table.finalY ?? finalY;
      },
    });

    return finalY;
  }

  /**
   * Resolves the theme and styles one layout is drawn with.
   *
   * @param layout - The section's layout.
   * @param isRtl - Whether the active language is right-to-left.
   * @param fontName - Font already loaded on the document.
   * @returns The autoTable options that style this layout, and nothing else.
   *
   * @remarks
   * Every layout's look is decided **here**, in its own branch, rather than in
   * the shared option object {@link _drawTable} builds. That is deliberate: a
   * style written into the shared object reaches all three layouts, so a change
   * meant for a banded record silently restyles the classic table and the
   * transposed one too. Each branch below can only affect its own layout.
   */
  private _resolveTableStyles(layout: ExportSectionLayout, isRtl: boolean, fontName: string): UserOptions {

    const baseStyles: UserOptions['styles'] = {
      fontSize: 9,
      font: fontName,
      halign: isRtl ? 'right' : 'left',
    };

    const readingEdge = isRtl ? 'right' as const : 'left' as const;

    // Transposed: left exactly as it was before the banded layouts existed —
    // the striped theme, no cell rules, and the emphasis carried by the field
    // column rather than by a header row it does not have.
    if (layout === 'fields') {
      return {
        styles: baseStyles,
        headStyles: { fontStyle: 'bold', halign: readingEdge },
      };
    }

    const ruledStyles: UserOptions['styles'] = {
      ...baseStyles,
      lineWidth: PdfExporterService._CELL_BORDER_WIDTH,
      lineColor: PdfExporterService._CELL_BORDER_COLOR,
    };

    // A band's header names the cell directly beneath it, so it is centred over
    // that cell rather than aligned to the reading edge of the page. Its rules
    // are drawn in its own fill, which keeps the band's outer edge one
    // unbroken box.
    if (layout === 'wrapped') {
      return {
        theme: 'grid',
        styles: ruledStyles,
        headStyles: {
          fontStyle: 'bold',
          fillColor: PdfExporterService._TABLE_HEAD_FILL,
          lineColor: PdfExporterService._TABLE_HEAD_FILL,
          halign: 'center',
        },
      };
    }

    // The classic table, styled as the reports this module replaces were: a
    // dark head band over a fully ruled grid, repeated at the top of every page
    // a long list spills onto.
    return {
      theme: 'grid',
      tableWidth: 'auto',
      showHead: 'everyPage',
      styles: {
        ...ruledStyles,
        // Every column is fitted to the page and its text wrapped, rather than
        // the table being cut into column slices that each take a page: a
        // record is only readable with all its fields side by side.
        cellWidth: 'auto',
        overflow: 'linebreak',
        // Top, not middle: a row whose one long cell wraps to three lines would
        // otherwise float its short cells into the middle of that height, and
        // the eye loses the line it was reading across.
        valign: 'top',
      },
      headStyles: {
        fontStyle: 'bold',
        fillColor: PdfExporterService._TABLE_HEAD_FILL,
        textColor: PdfExporterService._TABLE_HEAD_TEXT,
        lineColor: PdfExporterService._TABLE_HEAD_FILL,
        halign: 'center',
      },
    };
  }

  /**
   * Draws a section heading and reports where its table may begin.
   *
   * @param doc - The document being written.
   * @param title - The heading, already reordered for the active direction.
   * @param isRtl - Whether the active language is right-to-left.
   * @param y - Where the heading goes: the top of the content area for a
   *   section on its own page, lower down for a stacked one.
   * @returns The first y position below the heading.
   */
  private _drawSectionHeading(doc: jsPDF, title: string, isRtl: boolean, y: number): number {

    if (title.trim().length === 0) {
      return y;
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const x = isRtl ? pageWidth - PDF_CHROME.MARGIN : PDF_CHROME.MARGIN;

    doc.setFontSize(PdfExporterService._SECTION_TITLE_FONT_SIZE);
    doc.text(title, x, y, { align: isRtl ? 'right' : 'left' });

    return y + PdfExporterService._SECTION_TITLE_GAP;
  }

  /**
   * Stamps the header and footer onto every page.
   *
   * @param doc - The finished document.
   * @param options - Title, logo and direction.
   *
   * @remarks
   * Runs after the body is complete because the page total is not known until
   * then — which is exactly what the page counter needs.
   */
  private _stampChrome(doc: jsPDF, options: { title: string; logo: IExportLogo | null; isRtl: boolean }): void {

    const total = doc.getNumberOfPages();
    const order = (value: string): string => (options.isRtl ? applyPdfBidi(value) : value);
    const footerText = order(this._branding.footerText);

    for (let page = 1; page <= total; page += 1) {
      doc.setPage(page);

      drawPdfHeader(doc, options);
      drawPdfFooter(doc, {
        text: footerText,
        counter: order(this._translate.instant(this._translateKeyMap.PAGE_OF(), { current: page, total })),
        isRtl: options.isRtl,
      });
    }
  }

  /**
   * Hands the finished document to the browser.
   *
   * @param doc - The finished document.
   * @param request - The originating request.
   */
  private _output<TRow>(doc: jsPDF, request: IPdfExportRequest<TRow>): void {

    if (request.metadata?.print === true) {
      doc.autoPrint();
      window.open(doc.output('bloburl') as unknown as string, '_blank');

      return;
    }

    doc.save(ensureFileExtension(request.fileName, '.pdf'));
  }
}
